/**
 * The guided journeys (plan Section 3 "Option C", `journeys` todo): Monthly
 * Review, Cash Check, Board / Owner Prep, Improve Profitability, and Plan Next
 * Year. Each is defined declaratively as a list of steps; every step's `build`
 * is a pure function of the loaded shared contracts, so narration / charts /
 * takeaways stay semantically identical to the Brief and CFO Core. Terminal
 * steps hand off to existing surfaces via `JourneyHandoff`: Monthly Review and
 * Board Prep open the presentation flow, Improve Profitability deep-links into
 * the Ratios & Valuation workspace, and Plan Next Year deep-links into the
 * forecast/FVA workspace.
 */

import type { MetricComparisonRow } from '../../../data/ServerApiClient'
import {
  cardToTakeaway,
  cashWeeks,
  findMetricRow,
  formatAsOfMonth,
  formatCurrencyCompact,
  formatPercent,
  formatSignedPct,
  formatWeekLabel,
  num,
  pickComparison,
  selectInsights,
  trajectoryWithLabels,
} from './journeyData'
import type { JourneyChartSpec, JourneyDef, JourneyTakeaway } from './types'

const COLOR = {
  gold: '#b8892f',
  teal: '#176b87',
  green: '#1f8f5f',
  red: '#b42318',
  amber: '#d97706',
  navy: '#132238',
  slate: '#94a3b8',
}

/** "$935.6K, ▼ 14.8% vs prior month" style fragment for narration. */
const momFragment = (row: MetricComparisonRow | null): string => {
  if (!row) return 'not available'
  const mom = pickComparison(row, 'mom')
  const isRatio = row.value_kind === 'ratio'
  const latest = isRatio ? formatPercent(row.latest_value) : formatCurrencyCompact(row.latest_value)
  if (!mom || mom.delta_pct == null) return latest
  if (isRatio && mom.delta_abs != null) {
    const pts = mom.delta_abs * 100
    const dir = pts > 0 ? 'up' : pts < 0 ? 'down' : 'flat'
    return `${latest} (${dir} ${Math.abs(pts).toFixed(1)} pts vs prior month)`
  }
  const dir = mom.delta_pct > 0 ? 'up' : mom.delta_pct < 0 ? 'down' : 'flat'
  return `${latest} (${dir} ${formatSignedPct(Math.abs(mom.delta_pct))} vs prior month)`
}

/** Build a line chart from one or more trajectory metrics that share an axis. */
const trendChart = (
  rows: Array<{ row: MetricComparisonRow | null; label: string; color: string }>,
  format: JourneyChartSpec['format'],
): JourneyChartSpec | null => {
  let labels: string[] = []
  const series: JourneyChartSpec['series'] = []
  for (const r of rows) {
    const built = trajectoryWithLabels(r.row)
    if (built.values.length < 2) continue
    if (built.labels.length > labels.length) labels = built.labels
    series.push({ label: r.label, color: r.color, kind: 'line', values: built.values })
  }
  if (series.length === 0) return null
  return { labels, series, format }
}

const topTakeaways = (insights: Parameters<typeof selectInsights>[0], n = 3): JourneyTakeaway[] =>
  selectInsights(insights, {}, n).map(cardToTakeaway)

// ---------------------------------------------------------------------------
// Monthly Review — walk the month end-to-end (5 steps).
// ---------------------------------------------------------------------------
const monthlyReview: JourneyDef = {
  id: 'monthly_review',
  title: 'Monthly Review',
  subtitle: 'Walk this month end-to-end',
  steps: [
    {
      id: 'overview',
      title: 'Overview — how the month landed',
      shortTitle: 'Overview',
      dataSources: ['comparisons', 'insights'],
      build: (data) => {
        const revenue = findMetricRow(data.comparisons, 'revenue')
        const netIncome = findMetricRow(data.comparisons, 'net_income')
        const asOf = formatAsOfMonth(data.insights?.as_of || data.comparisons?.as_of)
        const attention = data.insights?.attention_count ?? 0
        const narration =
          `Here's how ${asOf} landed. Revenue came in at ${momFragment(revenue)}, and net income was ${momFragment(netIncome)}. ` +
          (attention > 0
            ? `I flagged ${attention} thing${attention === 1 ? '' : 's'} worth your attention this month — we'll walk each one.`
            : `Nothing needs urgent attention this month, so this is a quick confirm-and-share.`)
        return {
          narration,
          chartTitle: 'Revenue & net income trend',
          chartMeta: 'recent windows',
          chart: trendChart(
            [
              { row: revenue, label: 'Revenue', color: COLOR.gold },
              { row: netIncome, label: 'Net income', color: COLOR.green },
            ],
            'currency',
          ),
          takeaways: topTakeaways(data.insights, 3),
          copilotPrompt: `Give me a 3-sentence summary of how ${asOf} went for this business and the single most important thing to look at.`,
          depth: {
            label: 'Every headline metric, month over month',
            detail: (data.comparisons?.metrics || [])
              .map((m) => `${m.label}: ${momFragment(m)}`)
              .join('  •  ') || 'No comparison metrics available.',
          },
        }
      },
    },
    {
      id: 'profitability',
      title: 'Profitability — margins vs spend',
      shortTitle: 'Profitability',
      dataSources: ['comparisons', 'insights'],
      build: (data) => {
        const grossMargin = findMetricRow(data.comparisons, 'gross_margin')
        const netMargin = findMetricRow(data.comparisons, 'net_margin')
        const opex = findMetricRow(data.comparisons, 'operating_expenses')
        const narration =
          `Gross margin is ${momFragment(grossMargin)}, so the underlying work ${grossMargin && (grossMargin.latest_value ?? 0) > 0.3 ? 'is profitable' : 'is the thing to watch'}. ` +
          `Operating expenses were ${momFragment(opex)} — the gap between margin and spend is what moves net income. ` +
          `Confirm any margin swing is real before you present it.`
        const takeaways = selectInsights(
          data.insights,
          { tags: ['margin'], detectors: ['margin_cogs_sanity', 'period_variance'], metrics: ['operating_expenses', 'gross_profit', 'net_income', 'gross_margin'] },
          3,
        ).map(cardToTakeaway)
        return {
          narration,
          chartTitle: 'Margins over time',
          chartMeta: '% of sales',
          chart: trendChart(
            [
              { row: grossMargin, label: 'Gross margin', color: COLOR.teal },
              { row: netMargin, label: 'Net margin', color: COLOR.green },
            ],
            'percent',
          ),
          takeaways,
          copilotPrompt:
            'Did profitability change because of margins or because of operating expenses this month? Be specific about the drivers.',
          depth: {
            label: 'Why profit moved',
            detail:
              `Gross profit: ${momFragment(findMetricRow(data.comparisons, 'gross_profit'))}. ` +
              `Operating expenses: ${momFragment(opex)}. ` +
              `Net income: ${momFragment(findMetricRow(data.comparisons, 'net_income'))}.`,
          },
        }
      },
    },
    {
      id: 'cash',
      title: 'Cash — what the next weeks look like',
      shortTitle: 'Cash',
      dataSources: ['cash_planning', 'insights'],
      build: (data) => {
        const weeks = cashWeeks(data.cashPlanning)
        const ending = weeks.length ? num(weeks[weeks.length - 1].ending_cash) : null
        const opening = weeks.length ? num(weeks[0].opening_cash ?? weeks[0].ending_cash) : null
        const lowest = weeks.reduce<{ v: number; label: string } | null>((acc, w) => {
          const v = num(w.ending_cash)
          if (v == null) return acc
          if (!acc || v < acc.v) return { v, label: formatWeekLabel(w.week_start) }
          return acc
        }, null)
        const narration = weeks.length
          ? `Projected cash starts around ${formatCurrencyCompact(opening)} and ends near ${formatCurrencyCompact(ending)} across the next ${weeks.length} weeks. ` +
            (lowest ? `The tightest week is ${lowest.label} at about ${formatCurrencyCompact(lowest.v)}. ` : '') +
            `No surprises here means you can plan payouts with confidence.`
          : `No 13-week cash plan is built for this workbook yet. Open the Cash workspace to generate one.`
        return {
          narration,
          chartTitle: 'Projected ending cash',
          chartMeta: `${weeks.length}-week look ahead`,
          chart: weeks.length
            ? {
                labels: weeks.map((w) => formatWeekLabel(w.week_start)),
                series: [
                  {
                    label: 'Ending cash',
                    color: COLOR.green,
                    kind: 'line',
                    values: weeks.map((w) => num(w.ending_cash)),
                  },
                ],
                format: 'currency',
              }
            : null,
          takeaways: selectInsights(data.insights, { tags: ['cash', 'runway'], metrics: ['cash'] }, 2).map(cardToTakeaway),
          copilotPrompt: 'Walk me through the cash position for the next 13 weeks and any week I should plan around.',
        }
      },
    },
    {
      id: 'ar_risk',
      title: 'A/R & risk — who owes you',
      shortTitle: 'A/R & risk',
      dataSources: ['comparisons', 'insights'],
      build: (data) => {
        const ar = findMetricRow(data.comparisons, 'accounts_receivable')
        const arCards = selectInsights(data.insights, { tags: ['ar', 'risk'], detectors: ['ar_collectability_risk'], metrics: ['accounts_receivable'] }, 2)
        const narration = arCards.length
          ? `${arCards[0].headline}. ${arCards[0].body}`
          : `Accounts receivable is ${momFragment(ar)}. I don't see a collection risk flagged this month — receivables look collectable.`
        return {
          narration,
          chartTitle: 'Accounts receivable trend',
          chartMeta: 'recent windows',
          chart: trendChart([{ row: ar, label: 'Accounts receivable', color: COLOR.amber }], 'currency'),
          takeaways: arCards.map(cardToTakeaway),
          copilotPrompt: 'Is there any accounts-receivable collection risk this month, and which customers drive it?',
        }
      },
    },
    {
      id: 'wrap',
      title: 'Wrap & share — turn this into a packet',
      shortTitle: 'Wrap & share',
      dataSources: ['comparisons', 'insights'],
      build: (data) => {
        const asOf = formatAsOfMonth(data.insights?.as_of || data.comparisons?.as_of)
        const attention = data.insights?.attention_count ?? 0
        return {
          narration:
            `That's the month. ${asOf} is summarized into the headline numbers, profitability, cash, and A/R you just walked. ` +
            (attention > 0 ? `${attention} item${attention === 1 ? '' : 's'} need a note for your client. ` : '') +
            `I'll hand this off to the Client Financials presentation so you can review and share it.`,
          chartTitle: 'Ready to share',
          chart: null,
          takeaways: topTakeaways(data.insights, 4),
          handoff: { kind: 'presentation' },
          copilotPrompt: 'Draft a short plain-English summary of this month I can paste into a client note.',
        }
      },
    },
  ],
}

// ---------------------------------------------------------------------------
// Cash Check — 13-week look ahead (3 steps).
// ---------------------------------------------------------------------------
const cashCheck: JourneyDef = {
  id: 'cash_check',
  title: 'Cash Check',
  subtitle: '13-week look ahead',
  steps: [
    {
      id: 'position',
      title: "Today's cash position",
      shortTitle: 'Position',
      dataSources: ['cash_planning', 'comparisons'],
      build: (data) => {
        const weeks = cashWeeks(data.cashPlanning)
        const cashRow = findMetricRow(data.comparisons, 'cash')
        const opening = weeks.length ? num(weeks[0].opening_cash ?? weeks[0].ending_cash) : cashRow?.latest_value ?? null
        const narration = `You're starting with about ${formatCurrencyCompact(opening)} in cash. ` +
          (cashRow ? `That's ${momFragment(cashRow)}. ` : '') +
          `Next we'll project where it goes over the coming weeks.`
        return {
          narration,
          chartTitle: 'Cash on hand trend',
          chartMeta: 'recent windows',
          chart: trendChart([{ row: cashRow, label: 'Cash on hand', color: COLOR.green }], 'currency'),
          takeaways: selectInsights(data.insights, { tags: ['cash', 'runway'], metrics: ['cash'] }, 2).map(cardToTakeaway),
          copilotPrompt: 'How healthy is the current cash position relative to recent months?',
        }
      },
    },
    {
      id: 'lookahead',
      title: '13-week look ahead',
      shortTitle: '13-week',
      dataSources: ['cash_planning'],
      build: (data) => {
        const weeks = cashWeeks(data.cashPlanning)
        const receipts = weeks.reduce((s, w) => s + (num(w.receipts) ?? 0), 0)
        const disb = weeks.reduce((s, w) => s + (num(w.disbursements) ?? 0), 0)
        const ending = weeks.length ? num(weeks[weeks.length - 1].ending_cash) : null
        const lowest = weeks.reduce<{ v: number; label: string } | null>((acc, w) => {
          const v = num(w.ending_cash)
          if (v == null) return acc
          if (!acc || v < acc.v) return { v, label: formatWeekLabel(w.week_start) }
          return acc
        }, null)
        const narration = weeks.length
          ? `Over the next ${weeks.length} weeks I expect about ${formatCurrencyCompact(receipts)} in receipts against ${formatCurrencyCompact(disb)} in disbursements, ending near ${formatCurrencyCompact(ending)}. ` +
            (lowest ? `Plan around ${lowest.label}, the tightest week at roughly ${formatCurrencyCompact(lowest.v)}.` : '')
          : `No 13-week cash plan exists yet. Generate one from the Cash workspace, then re-run this check.`
        return {
          narration,
          chartTitle: 'Receipts vs disbursements & ending cash',
          chartMeta: `${weeks.length}-week look ahead`,
          chart: weeks.length
            ? {
                labels: weeks.map((w) => formatWeekLabel(w.week_start)),
                series: [
                  { label: 'Receipts', color: COLOR.green, kind: 'bars', values: weeks.map((w) => num(w.receipts)) },
                  {
                    label: 'Disbursements',
                    color: COLOR.red,
                    kind: 'bars',
                    values: weeks.map((w) => {
                      const d = num(w.disbursements)
                      return d == null ? null : -Math.abs(d)
                    }),
                  },
                  { label: 'Ending cash', color: COLOR.navy, kind: 'line', values: weeks.map((w) => num(w.ending_cash)) },
                ],
                format: 'currency',
              }
            : null,
          copilotPrompt: 'Which weeks in the 13-week cash forecast are tightest, and what is driving the disbursements?',
          depth: {
            label: 'Weeks needing review',
            detail:
              weeks
                .filter((w) => w.needs_review)
                .map((w) => `${formatWeekLabel(w.week_start)} (ending ${formatCurrencyCompact(num(w.ending_cash))})`)
                .join('  •  ') || 'No weeks are flagged for review.',
          },
        }
      },
    },
    {
      id: 'watch',
      title: 'What to watch',
      shortTitle: 'Watch',
      dataSources: ['cash_planning', 'insights'],
      build: (data) => {
        const weeks = cashWeeks(data.cashPlanning)
        const reviewCount = weeks.filter((w) => w.needs_review).length
        const cards = selectInsights(data.insights, { tags: ['cash', 'runway', 'risk'], metrics: ['cash'] }, 3)
        const narration =
          (reviewCount > 0
            ? `${reviewCount} week${reviewCount === 1 ? '' : 's'} in the plan still need a review before you rely on them. `
            : `Every week in the plan has been reviewed. `) +
          (cards.length ? `${cards[0].headline}.` : `Cash looks steady with no flagged risks.`)
        return {
          narration,
          chartTitle: 'Net cash change by week',
          chartMeta: `${weeks.length}-week look ahead`,
          chart: weeks.length
            ? {
                labels: weeks.map((w) => formatWeekLabel(w.week_start)),
                series: [
                  {
                    label: 'Net cash change',
                    color: COLOR.teal,
                    kind: 'bars',
                    values: weeks.map((w) => num(w.net_cash_change)),
                  },
                ],
                format: 'currency',
              }
            : null,
          takeaways: cards.map(cardToTakeaway),
          copilotPrompt: 'What actions would most improve cash over the next quarter for this business?',
        }
      },
    },
  ],
}

// ---------------------------------------------------------------------------
// Board / Owner Prep — assemble a shareable packet (4 steps).
// ---------------------------------------------------------------------------
const boardPrep: JourneyDef = {
  id: 'board_prep',
  title: 'Board / Owner Prep',
  subtitle: 'Build a shareable packet',
  steps: [
    {
      id: 'story',
      title: 'The headline story',
      shortTitle: 'Story',
      dataSources: ['comparisons', 'insights'],
      build: (data) => {
        const asOf = formatAsOfMonth(data.insights?.as_of || data.comparisons?.as_of)
        const attention = data.insights?.attention_count ?? 0
        const top = selectInsights(data.insights, {}, 1)[0]
        const narration =
          `For ${asOf}, the story to lead with is: ${top ? top.headline.toLowerCase() : 'the business is steady'}. ` +
          (attention > 0
            ? `There ${attention === 1 ? 'is' : 'are'} ${attention} item${attention === 1 ? '' : 's'} the board should know about.`
            : `Nothing requires escalation, which is itself a clean message.`)
        return {
          narration,
          chartTitle: 'What the board should know',
          chart: null,
          takeaways: topTakeaways(data.insights, 4),
          copilotPrompt: `Write a 2-sentence opening for a board update covering ${asOf}.`,
        }
      },
    },
    {
      id: 'numbers',
      title: 'Numbers that matter',
      shortTitle: 'Numbers',
      dataSources: ['comparisons'],
      build: (data) => {
        const revenue = findMetricRow(data.comparisons, 'revenue')
        const netIncome = findMetricRow(data.comparisons, 'net_income')
        const ebitda = findMetricRow(data.comparisons, 'ebitda')
        const narration =
          `The numbers the board cares about: revenue ${momFragment(revenue)}, net income ${momFragment(netIncome)}, EBITDA ${momFragment(ebitda)}. ` +
          `These three frame performance without overwhelming the room.`
        return {
          narration,
          chartTitle: 'Revenue, net income & EBITDA',
          chartMeta: 'recent windows',
          chart: trendChart(
            [
              { row: revenue, label: 'Revenue', color: COLOR.gold },
              { row: netIncome, label: 'Net income', color: COLOR.green },
              { row: ebitda, label: 'EBITDA', color: COLOR.teal },
            ],
            'currency',
          ),
          copilotPrompt: 'Which 3-4 metrics best tell this quarter\'s story to a board, and why?',
          depth: {
            label: 'Full metric set',
            detail:
              (data.comparisons?.metrics || []).map((m) => `${m.label}: ${momFragment(m)}`).join('  •  ') ||
              'No comparison metrics available.',
          },
        }
      },
    },
    {
      id: 'risks',
      title: 'Risks & asks',
      shortTitle: 'Risks',
      dataSources: ['insights', 'comparisons'],
      build: (data) => {
        const cards = selectInsights(data.insights, { tags: ['risk', 'cash', 'runway', 'ar'], detectors: ['ar_collectability_risk', 'cash_runway', 'low_confidence_forecast'] }, 3)
        const narration = cards.length
          ? `Bring these to the board: ${cards.map((c) => c.headline).join('; ')}. Frame each with the action you're taking.`
          : `No material risks to escalate this period. You can present a clean, confident update.`
        return {
          narration,
          chartTitle: 'Risk register',
          chart: null,
          takeaways: cards.map(cardToTakeaway),
          copilotPrompt: 'Turn the current risks into board-ready talking points with a recommended action for each.',
        }
      },
    },
    {
      id: 'assemble',
      title: 'Assemble the packet',
      shortTitle: 'Assemble',
      dataSources: ['comparisons', 'insights'],
      build: () => ({
        narration:
          `Everything's ready. I'll assemble the headline story, the numbers, and the risks into the Client Financials presentation, ` +
          `where you can reorder, edit, and share it as a board packet.`,
        chartTitle: 'Ready to assemble',
        chart: null,
        handoff: { kind: 'presentation' },
        copilotPrompt: 'Outline the slides for a board packet based on this workbook.',
      }),
    },
  ],
}

// ---------------------------------------------------------------------------
// Improve Profitability — diagnose margin/expense drivers, then act (4 steps).
// Reuses the comparison engine (margins, opex, profit) and the insight spine
// (margin/expense detectors); the final step hands off to the existing Ratios
// & Valuation workspace where the margin/efficiency ratios already live.
// ---------------------------------------------------------------------------
const improveProfitability: JourneyDef = {
  id: 'improve_profitability',
  title: 'Improve Profitability',
  subtitle: 'Find the levers that move profit',
  steps: [
    {
      id: 'diagnose',
      title: 'Diagnose — where profit stands',
      shortTitle: 'Diagnose',
      dataSources: ['comparisons', 'insights'],
      build: (data) => {
        const netIncome = findMetricRow(data.comparisons, 'net_income')
        const netMargin = findMetricRow(data.comparisons, 'net_margin')
        const asOf = formatAsOfMonth(data.insights?.as_of || data.comparisons?.as_of)
        const narration =
          `Let's improve profitability. As of ${asOf}, net income is ${momFragment(netIncome)} and net margin is ${momFragment(netMargin)}. ` +
          `Profit moves for two reasons — the margin on the work, or the spend around it — so we'll separate the two before deciding where to act.`
        return {
          narration,
          chartTitle: 'Net income & net margin',
          chartMeta: 'recent windows',
          chart: trendChart(
            [
              { row: netIncome, label: 'Net income', color: COLOR.green },
              { row: netMargin, label: 'Net margin', color: COLOR.teal },
            ],
            'currency',
          ),
          takeaways: selectInsights(
            data.insights,
            { tags: ['margin', 'profit'], detectors: ['period_variance'], metrics: ['net_income', 'net_margin', 'gross_profit'] },
            3,
          ).map(cardToTakeaway),
          copilotPrompt:
            'Is profitability trending up or down right now, and is it driven by margins or by operating expenses? Be specific.',
          depth: {
            label: 'Profit components',
            detail:
              `Gross profit: ${momFragment(findMetricRow(data.comparisons, 'gross_profit'))}. ` +
              `Operating expenses: ${momFragment(findMetricRow(data.comparisons, 'operating_expenses'))}. ` +
              `Net income: ${momFragment(netIncome)}.`,
          },
        }
      },
    },
    {
      id: 'margins',
      title: 'Margins — is the work profitable?',
      shortTitle: 'Margins',
      dataSources: ['comparisons', 'insights'],
      build: (data) => {
        const grossMargin = findMetricRow(data.comparisons, 'gross_margin')
        const netMargin = findMetricRow(data.comparisons, 'net_margin')
        const grossProfit = findMetricRow(data.comparisons, 'gross_profit')
        const healthy = grossMargin && (grossMargin.latest_value ?? 0) > 0.3
        const narration =
          `Gross margin is ${momFragment(grossMargin)} — ${healthy ? 'the underlying work is profitable' : 'this is the first lever to watch'}. ` +
          `Gross profit came in at ${momFragment(grossProfit)}. ` +
          `If margin is slipping, the cause is pricing or cost of goods, not overhead — confirm any swing is real before you chase it.`
        return {
          narration,
          chartTitle: 'Gross & net margin',
          chartMeta: '% of sales',
          chart: trendChart(
            [
              { row: grossMargin, label: 'Gross margin', color: COLOR.teal },
              { row: netMargin, label: 'Net margin', color: COLOR.green },
            ],
            'percent',
          ),
          takeaways: selectInsights(
            data.insights,
            { tags: ['margin'], detectors: ['margin_cogs_sanity', 'period_variance'], metrics: ['gross_margin', 'gross_profit'] },
            3,
          ).map(cardToTakeaway),
          copilotPrompt:
            'Did gross margin change because of pricing or cost of goods sold this period? What is the most likely driver?',
        }
      },
    },
    {
      id: 'expenses',
      title: 'Expenses — where the money goes',
      shortTitle: 'Expenses',
      dataSources: ['comparisons', 'insights'],
      build: (data) => {
        const opex = findMetricRow(data.comparisons, 'operating_expenses')
        const revenue = findMetricRow(data.comparisons, 'revenue')
        const narration =
          `Operating expenses were ${momFragment(opex)} against revenue of ${momFragment(revenue)}. ` +
          `The gap between the two is what reaches the bottom line, so opex creeping faster than revenue is the quiet profit killer. ` +
          `Next we'll turn this into concrete levers.`
        return {
          narration,
          chartTitle: 'Revenue vs operating expenses',
          chartMeta: 'recent windows',
          chart: trendChart(
            [
              { row: revenue, label: 'Revenue', color: COLOR.gold },
              { row: opex, label: 'Operating expenses', color: COLOR.red },
            ],
            'currency',
          ),
          takeaways: selectInsights(
            data.insights,
            { tags: ['expense', 'opex', 'spend'], detectors: ['period_variance', 'expense_growth'], metrics: ['operating_expenses'] },
            3,
          ).map(cardToTakeaway),
          copilotPrompt:
            'Which operating-expense categories grew fastest, and which are the best candidates to trim without hurting the business?',
          depth: {
            label: 'Spend vs sales',
            detail:
              `Revenue: ${momFragment(revenue)}. ` +
              `Operating expenses: ${momFragment(opex)}. ` +
              `Gross profit: ${momFragment(findMetricRow(data.comparisons, 'gross_profit'))}.`,
          },
        }
      },
    },
    {
      id: 'levers',
      title: 'Levers — turn diagnosis into action',
      shortTitle: 'Levers',
      dataSources: ['comparisons', 'insights'],
      build: (data) => {
        const grossMargin = findMetricRow(data.comparisons, 'gross_margin')
        const opex = findMetricRow(data.comparisons, 'operating_expenses')
        return {
          narration:
            `You've separated margin (${momFragment(grossMargin)}) from spend (${momFragment(opex)}). ` +
            `The levers are pricing, cost of goods, and overhead discipline. ` +
            `I'll open the Ratios & Valuation workspace, where the margin and efficiency ratios let you size each lever against peers.`,
          chartTitle: 'Margin vs spend',
          chartMeta: 'recent windows',
          chart: trendChart(
            [
              { row: grossMargin, label: 'Gross margin', color: COLOR.teal },
              { row: opex, label: 'Operating expenses', color: COLOR.red },
            ],
            'currency',
          ),
          takeaways: selectInsights(
            data.insights,
            { tags: ['margin', 'expense', 'profit'], metrics: ['net_income', 'gross_margin', 'operating_expenses'] },
            3,
          ).map(cardToTakeaway),
          handoff: { kind: 'cfo_core', target: { tab: 'ratios_valuation', view: null, params: {} }, label: 'Open ratios & valuation' },
          copilotPrompt:
            'Give me the three highest-impact levers to improve profitability for this business, ranked, with the expected effect of each.',
        }
      },
    },
  ],
}

// ---------------------------------------------------------------------------
// Plan Next Year — turn this year's trend into a next-year budget (4 steps).
// Reuses the comparison engine (revenue/expense/profit baseline) and the
// insight spine (forward-looking risk detectors); the final step hands off to
// the existing forecast/FVA workspace (CFO Core `insights` group) where the ML
// forecast and scenarios live.
// ---------------------------------------------------------------------------
const planNextYear: JourneyDef = {
  id: 'plan_next_year',
  title: 'Plan Next Year',
  subtitle: 'Draft next year from this year',
  steps: [
    {
      id: 'baseline',
      title: 'Baseline — start from this year',
      shortTitle: 'Baseline',
      dataSources: ['comparisons', 'insights'],
      build: (data) => {
        const revenue = findMetricRow(data.comparisons, 'revenue')
        const netIncome = findMetricRow(data.comparisons, 'net_income')
        const asOf = formatAsOfMonth(data.insights?.as_of || data.comparisons?.as_of)
        const narration =
          `A good budget starts from where you actually are. Through ${asOf}, revenue is ${momFragment(revenue)} and net income is ${momFragment(netIncome)}. ` +
          `That trailing trend is the anchor for next year — we'll carry it forward and adjust.`
        return {
          narration,
          chartTitle: 'Revenue & net income',
          chartMeta: 'recent windows',
          chart: trendChart(
            [
              { row: revenue, label: 'Revenue', color: COLOR.gold },
              { row: netIncome, label: 'Net income', color: COLOR.green },
            ],
            'currency',
          ),
          takeaways: topTakeaways(data.insights, 3),
          copilotPrompt:
            `Summarize this year's revenue and profit trend as the starting point for next year's budget, in plain language.`,
        }
      },
    },
    {
      id: 'assumptions',
      title: 'Assumptions — growth & costs',
      shortTitle: 'Assumptions',
      dataSources: ['comparisons'],
      build: (data) => {
        const revenue = findMetricRow(data.comparisons, 'revenue')
        const opex = findMetricRow(data.comparisons, 'operating_expenses')
        const grossMargin = findMetricRow(data.comparisons, 'gross_margin')
        const revYoy = pickComparison(revenue, 'yoy')
        const growthHint =
          revYoy && revYoy.delta_pct != null
            ? `Revenue is ${formatSignedPct(revYoy.delta_pct)} year over year, a reasonable first growth assumption. `
            : ''
        const narration =
          `Now set the dials. ${growthHint}` +
          `Hold gross margin near ${momFragment(grossMargin)} unless you're changing pricing, and grow operating expenses (${momFragment(opex)}) more slowly than revenue to expand profit. ` +
          `These three assumptions — growth, margin, and expense discipline — frame the whole plan.`
        return {
          narration,
          chartTitle: 'Revenue vs operating expenses',
          chartMeta: 'recent windows',
          chart: trendChart(
            [
              { row: revenue, label: 'Revenue', color: COLOR.gold },
              { row: opex, label: 'Operating expenses', color: COLOR.red },
            ],
            'currency',
          ),
          copilotPrompt:
            `What revenue growth rate, gross margin, and expense growth should I assume for next year's budget based on the trend?`,
          depth: {
            label: 'Year-over-year baseline',
            detail:
              `Revenue: ${momFragment(revenue)}. ` +
              `Operating expenses: ${momFragment(opex)}. ` +
              `Gross margin: ${momFragment(grossMargin)}. ` +
              `Net income: ${momFragment(findMetricRow(data.comparisons, 'net_income'))}.`,
          },
        }
      },
    },
    {
      id: 'watchouts',
      title: 'Watch-outs — stress-test the plan',
      shortTitle: 'Watch-outs',
      dataSources: ['insights', 'comparisons'],
      build: (data) => {
        const cards = selectInsights(
          data.insights,
          { tags: ['risk', 'cash', 'runway', 'forecast'], detectors: ['low_confidence_forecast', 'cash_runway', 'ar_collectability_risk'] },
          3,
        )
        const narration = cards.length
          ? `Before you commit, build in buffer for these: ${cards.map((c) => c.headline).join('; ')}. A budget that ignores known risks breaks in the first surprise.`
          : `No material risks are flagged this period, so a straight-line plan off the baseline is a fair starting point — still keep a modest contingency.`
        return {
          narration,
          chartTitle: 'Risks to plan around',
          chart: null,
          takeaways: cards.map(cardToTakeaway),
          copilotPrompt:
            'What risks should next year\'s budget account for, and where should I build in buffer or a contingency?',
        }
      },
    },
    {
      id: 'forecast',
      title: 'Forecast — build the numbers',
      shortTitle: 'Forecast',
      dataSources: ['comparisons'],
      build: (data) => {
        const revenue = findMetricRow(data.comparisons, 'revenue')
        return {
          narration:
            `You've got the baseline, the assumptions, and the watch-outs. ` +
            `I'll open the forecast workspace, where the ML forecast and scenario presets turn this into a month-by-month next-year plan you can adjust and compare. ` +
            `Anchor revenue near ${momFragment(revenue)} as you go.`,
          chartTitle: 'Ready to forecast',
          chart: null,
          handoff: { kind: 'cfo_core', target: { tab: 'insights', view: null, params: {} }, label: 'Open the forecast workspace' },
          copilotPrompt:
            'Outline a next-year budget with monthly revenue and expense targets I can refine in the forecast workspace.',
        }
      },
    },
  ],
}

export const JOURNEY_DEFINITIONS: Record<string, JourneyDef> = {
  monthly_review: monthlyReview,
  cash_check: cashCheck,
  board_prep: boardPrep,
  improve_profitability: improveProfitability,
  plan_next_year: planNextYear,
}

export const getJourneyDefinition = (id: string): JourneyDef | null => JOURNEY_DEFINITIONS[id] || null
